import React, {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle
} from "react";
import APIRequest from "../../../../../Services/commonAPIConsumer";
import { useDispatch, useSelector } from "react-redux";
import {
    setCustomFilterOptions,
    currentSelectedLanguage
} from "../../../../../StateManagement/actions";

const FilterLayoutDragDrop = forwardRef((props, ref) => {

    const selectedLanguage = useSelector(currentSelectedLanguage);

    const [available, setAvailable] = useState([]);
    const [selected, setSelected] = useState([]);
    const [dragItem, setDragItem] = useState(null);
    const [dragFrom, setDragFrom] = useState(null);
    const [checkedAvailable, setCheckedAvailable] = useState([]);
    const [checkedSelected, setCheckedSelected] = useState([]);

    const dispatch = useDispatch();

    const DEFAULT_SELECTED_FILTERS = [
        { name: "isDocumentId", label: selectedLanguage.LstLanguageCodes.UI_DocumentID, value: "DocumentId" },
        { name: "isSubject", label: `${selectedLanguage.LstLanguageCodes.UI_Subject}/${selectedLanguage.LstLanguageCodes.UI_Description}`, value: "SubjectDescription" },
        { name: "isFileName", label: `${selectedLanguage.LstLanguageCodes.UI_File}/${selectedLanguage.LstLanguageCodes.UI_DocumentName}/${selectedLanguage.LstLanguageCodes.UI_SizeKB}`, value: "FileName" },
        { name: "isReaderEmail", label: selectedLanguage.LstLanguageCodes.UI_ReaderEmail, value: "ReaderEmail" },
        { name: "isAccessControl", label: selectedLanguage.LstLanguageCodes.UI_AccessControl, value: "AccessControl" },
        { name: "isStatus", label: selectedLanguage.LstLanguageCodes.UI_Status, value: "Status" },
    ];
    
    const DEFAULT_AVAILABLE_FILTERS = [
        { name: "isDomains", label: selectedLanguage.LstLanguageCodes.UI_Domains, value: "Domains" },
        { name: "isTemplates", label: selectedLanguage.LstLanguageCodes.UI_Templates, value: "Templates" },
        { name: "isLaunches", label: selectedLanguage.LstLanguageCodes.UI_Launches, value: "Launches" },
        { name: "isExpires", label: selectedLanguage.LstLanguageCodes.UI_Expires, value: "Expires" },
    ];

    // LOAD FROM DB WITH ORDER
    useEffect(() => {

        if (!props.customFilterOptionsArray) return;

        const selectedList = [];
        const availableList = [];

        const allFilters = [
            ...DEFAULT_SELECTED_FILTERS,
            ...DEFAULT_AVAILABLE_FILTERS
        ];

        allFilters.forEach(filter => {

            const value =
                props.customFilterOptionsArray[filter.name];

            if (value?.visible) {
                selectedList.push({
                    ...filter,
                    order: value.order
                });
            } else {
                availableList.push(filter);
            }
        });

        // Sort by saved order
        selectedList.sort((a, b) => a.order - b.order);

        setSelected(selectedList);
        setAvailable(availableList);

    }, [props.customFilterOptionsArray]);

    useImperativeHandle(ref, () => ({
        saveCustomFilter,
        restoreDefaults
    }));

    const restoreDefaults = () => {
        setSelected(DEFAULT_SELECTED_FILTERS);
        setAvailable(DEFAULT_AVAILABLE_FILTERS);
        setCheckedAvailable([]);
        setCheckedSelected([]);
    };

    // SAVE WITH ORDER
    const saveCustomFilter = async () => {
        try {
            props.clearFilters();
            if (!selected || selected.length !== 6) {
                return {
                    success: false,
                    message: "Please select exactly six search filters."
                };
            }
            const CustomFilterOptions = {};
            // Selected with order
            selected.forEach((item, index) => {
                CustomFilterOptions[item.value] = {
                    visible: true,
                    order: index + 1
                };
            });
            // Available without order
            available.forEach(item => {
                CustomFilterOptions[item.value] = {
                    visible: false,
                    order: 0
                };
            });
            const formData = new FormData();
            formData.append(
                "CustomFilterOptions",
                JSON.stringify(CustomFilterOptions)
            );

            const response = await APIRequest.POST(
                formData,
                `${process.env.REACT_APP_API + "/docs/CustomFilterCreation"}`
            );

            dispatch(setCustomFilterOptions(response.customFilterOptions));

            return response;

        } catch (error) {
            console.error("Error saving filters:", error);
            throw error;
        }
    };

    const toggleChecked = (list, name) => {
        if (list === "available") {
            setCheckedAvailable(prev =>
                prev.includes(name)
                    ? prev.filter(x => x !== name)
                    : [...prev, name]
            );
        } else {
            setCheckedSelected(prev =>
                prev.includes(name)
                    ? prev.filter(x => x !== name)
                    : [...prev, name]
            );
        }
    };

    const handleDragStart = (item, listName) => {
        setDragItem(item);
        setDragFrom(listName);
    };

 const handleDrop = (listName, index = null) => {

    if (!dragItem) return;
    // ✅ SAME LIST (reorder only)
    if (dragFrom === listName) {

        const list =
            listName === "available"
                ? [...available]
                : [...selected];

        const oldIndex =
            list.findIndex(x => x.name === dragItem.name);

        list.splice(oldIndex, 1);

        if (index === null) {
            list.push(dragItem);
        } else {
            list.splice(index, 0, dragItem);
        }

        listName === "available"
            ? setAvailable(list)
            : setSelected(list);

    } 
    // ✅ DIFFERENT LIST (move + uncheck)
    else {

        let sourceList =
            dragFrom === "available"
                ? [...available]
                : [...selected];

        let destList =
            listName === "available"
                ? [...available]
                : [...selected];

        sourceList =
            sourceList.filter(x => x.name !== dragItem.name);

        destList =
            destList.filter(x => x.name !== dragItem.name);

        if (index === null) {
            destList.push(dragItem);
        } else {
            destList.splice(index, 0, dragItem);
        }

        dragFrom === "available"
            ? setAvailable(sourceList)
            : setSelected(sourceList);

        listName === "available"
            ? setAvailable(destList)
            : setSelected(destList);

        // 🔥 UNCHECK ONLY WHEN MOVING BETWEEN LISTS
        if (dragFrom === "available") {
            setCheckedAvailable(prev =>
                prev.filter(name => name !== dragItem.name)
            );
        } else {
            setCheckedSelected(prev =>
                prev.filter(name => name !== dragItem.name)
            );
        }
    }

    setDragItem(null);
    setDragFrom(null);
};


    const moveToSelected = () => {
        const moveItems =
            available.filter(x =>
                checkedAvailable.includes(x.name)
            );

        setSelected(prev => [...prev, ...moveItems]);

        setAvailable(prev =>
            prev.filter(x =>
                !checkedAvailable.includes(x.name)
            )
        );

        setCheckedAvailable([]);
    };

    const moveToAvailable = () => {
        const moveItems =
            selected.filter(x =>
                checkedSelected.includes(x.name)
            );

        setAvailable(prev => [...prev, ...moveItems]);

        setSelected(prev =>
            prev.filter(x =>
                !checkedSelected.includes(x.name)
            )
        );

        setCheckedSelected([]);
    };

    const shouldDisableButton = (type) =>
        type === "available"
            ? checkedAvailable.length === 0
            : checkedSelected.length === 0;

    const renderList = (items, listName, checked) => (
        <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(listName)}
            style={{
                border: "1px solid #ccc",
                padding: 10,
                minWidth: 300,
                maxHeight: 400,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 4
            }}
        >
            <h4>
                {listName === "available"
                    ? selectedLanguage.LstLanguageCodes.UI_AvailableFilters
                    : selectedLanguage.LstLanguageCodes.UI_SelectedFilters}
            </h4>

            {items.length === 0
                ? <div style={{ textAlign: "center", padding: 10 }}>No filters</div>
                : items.map((item, index) => {

                    const isChecked =
                        checked.includes(item.name);

                    return (
                        <div
                            key={item.name}
                            draggable
                            onDragStart={() =>
                                handleDragStart(item, listName)
                            }
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                                e.stopPropagation();
                                handleDrop(listName, index);
                            }}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                backgroundColor:
                                    isChecked ? "#d0ebff" : "#fafafa",
                                padding: "6px 8px",
                                borderRadius: 4,
                                border: "1px solid #ddd",
                                cursor: "move"
                            }}
                        >
                            <span
                                style={{ cursor: "pointer", flexGrow: 1, display: "flex" }}
                                onClick={() =>
                                    toggleChecked(listName, item.name)
                                }
                            >
                                <input
                                    type="checkbox"
                                    checked={isChecked}
                                    readOnly
                                    style={{ marginRight: 8, cursor: 'pointer', pointerEvents: 'none' }}
                                />
                                <span style={{cursor:'move', marginRight: 8 }}>⋮⋮</span>
                                {item.label}
                            </span>
                        </div>
                    );
                })}
        </div>
    );

    return (
        <div style={{ display: "flex", gap: 20, padding: 20 }}>

            {renderList(available, "available", checkedAvailable)}

            <div style={{ display: "flex", alignItems: "center" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <button
                        className="btn btn-primary btn-arrow-items"
                        disabled={shouldDisableButton("available")}
                        onClick={moveToSelected}
                    >
                        <span className="material-icons">double_arrow</span>
                    </button>

                    <button
                        className="btn btn-danger btn-arrow-items"
                        disabled={shouldDisableButton("selected")}
                        onClick={moveToAvailable}
                    >
                        <span className="material-icons rotate-180">
                            double_arrow
                        </span>
                    </button>
                </div>
            </div>

            {renderList(selected, "selected", checkedSelected)}

        </div>
    );
});

export default FilterLayoutDragDrop;
